// The timings and small rules behind the player screens' moments (spec v2 section 3), kept out of the components
// so they are unit-tested on their own and the screens hot-reload cleanly.
import type { LeaderboardRow } from '@shared/types';
import type { SfxName } from '@/audio/sfx';

/** M4's cue for each second left: a tick, the heartbeat in the last five, a haptic tick in the last three, the buzzer at 0. Silent once locked in. */
export function secondCue(left: number, locked: boolean): { sound?: SfxName; haptic: boolean } {
  if (locked || left < 0) return { haptic: false };
  if (left === 0) return { sound: 'timeup', haptic: false };
  return { sound: left <= 5 ? 'heartbeat' : 'tick', haptic: left <= 3 };
}

/** Where the right answer sits on screen, as fractions of the viewport, so the confetti bursts from the tile. */
export function originOf(container: HTMLElement | null, optionId: string): { x: number; y: number } | undefined {
  if (!container || !/^[\w-]+$/.test(optionId) || typeof window === 'undefined') return undefined;
  const el = container.querySelector<HTMLElement>(`[data-option-id="${optionId}"], [data-option="${optionId}"], [data-id="${optionId}"]`)
    ?? container.querySelector<HTMLElement>('[data-state="correct"]');
  const box = el?.getBoundingClientRect(); const w = window.innerWidth; const h = window.innerHeight;
  if (!box || !box.width || !w || !h) return undefined;
  return { x: Math.min(1, Math.max(0, (box.left + box.width / 2) / w)), y: Math.min(1, Math.max(0, (box.top + box.height / 2) / h)) };
}

/**
 * The board as it stood before this question: rows in their previous order, with their previous ranks and, when this phone
 * saw the last board, their previous scores. Rendering this first and then the real board lets each row slide (FLIP) to its new place.
 */
export function beforeBoard(top: LeaderboardRow[], prevTop?: LeaderboardRow[]): LeaderboardRow[] {
  const prevScore = new Map((prevTop ?? []).map((r) => [r.playerId, r.score]));
  return [...top]
    .sort((a, b) => (a.prevRank ?? Number.MAX_SAFE_INTEGER) - (b.prevRank ?? Number.MAX_SAFE_INTEGER) || a.rank - b.rank)
    .map((r) => ({ ...r, rank: r.prevRank ?? r.rank, score: prevScore.get(r.playerId) ?? r.score, prevRank: undefined }));
}
export const sameOrder = (a: LeaderboardRow[], b: LeaderboardRow[]) => a.length === b.length && a.every((r, i) => r.playerId === b[i].playerId && r.score === b[i].score);
/** True when at least one row changes place between the two boards (scores aside). */
export const reorders = (a: LeaderboardRow[], b: LeaderboardRow[]) => a.length !== b.length || a.some((r, i) => r.playerId !== b[i].playerId);
/**
 * The scores to animate from: the previous board this phone saw; after the first question, nothing for everybody
 * (every score starts at zero, so the first board counts up from it); otherwise unknown.
 */
export function scoreBasis(top: LeaderboardRow[], index: number, prevTop?: LeaderboardRow[]): LeaderboardRow[] | undefined {
  if (prevTop) return prevTop;
  return index === 0 ? top.map((r) => ({ ...r, score: 0 })) : undefined;
}
export const isHalfway = (index: number, total: number) => total >= 6 && index + 1 === Math.ceil(total / 2);
/** The leaderboard holds the old order this long before the rows move. */
export const FLIP_AFTER_MS = 650;

/** The ceremony's beats, in ms from the podium opening: third rises, then second, a pause, then first, then your card. */
export const BEATS = { third: 450, second: 1150, first: 1950, card: 3300 } as const;

/** The join tap's moment (M1) plays before the game opens. */
export const JOIN_CELEBRATION_MS = 700;
/** How long "Finding your place" may take before the prefilled form is offered alongside it. */
export const RESUME_PATIENCE_MS = 6000;

/** /me is read by every phone at each reveal; a little jitter spreads that load without delaying the moment itself. */
export const ME_JITTER_MS = 500;
/** A failed read at a reveal is tried this many times in all, waiting a little longer each time. */
export const ME_ATTEMPTS = 3;
export const ME_RETRY_MS = 1200;
/** A /me younger than this (for example from the join screen's resume check) is reused instead of read again. */
export const ME_FRESH_MS = 5000;
/** Why the game sent a player back to the join page: the session ran out, the seat was not there, or (9 Oct) another device took the seat. */
export type ResumeProblem = 'expired' | 'missing' | 'moved';
/** What the game hands the join page when it sends a player back: why, and the name to prefill. */
export interface ResumeState { resume: ResumeProblem; name?: string }
